/**
 * Field names on the map — and the collision culling Leaflet does not have.
 *
 * MapLibre drew these as a `symbol` layer, and the valuable part was not the typography: it
 * was that a name which does not fit is DROPPED rather than printed across its neighbour. On
 * a dense roster that difference is the difference between a map and a smudge. Leaflet has
 * no symbol layer and `L.tooltip({ permanent: true })` prints everything unconditionally, so
 * the culling is written here: a greedy pass in screen space, the selected field first, and
 * a name that overlaps something already kept is not drawn.
 *
 * It is an approximation in one place only — the width of a name is estimated from its
 * length rather than measured, because measuring means a layout pass per label per frame.
 * The estimate errs wide, so the failure mode is "one name too few", not "two names on top
 * of each other".
 *
 * `MIN_ZOOM` is 10.5 as the brief has it (below that a field is under 4 px across and its
 * name is litter). Leaflet's default `zoomSnap` is 1, so in practice this means z11.
 */

import * as L from "leaflet";

import type { Field } from "../api/types";
import type { Theme } from "../hooks/useTheme";
import { bboxOf } from "../lib/geo";
import { MAP_LABEL_HALO, MAP_LABEL_INK, MAP_LABEL_PAINT } from "./basemap";
import { PANES } from "./panes";

export const FIELD_LABEL_MIN_ZOOM = 10.5;

/** Roughly one em of the 12 px label; the estimate leans wide on purpose. */
const CHAR_WIDTH = 7;
const LABEL_HEIGHT = 18;
/** `text-max-width: 8` was 8 em; the same ceiling, in characters. */
const MAX_CHARS = 18;

export interface LabelBox {
  id: number;
  /** Centre of the label in container pixels. */
  x: number;
  y: number;
  width: number;
  height: number;
  /** Lower wins the place: the selected field is 0, everything else 1. */
  priority: number;
}

const overlaps = (a: LabelBox, b: LabelBox): boolean =>
  Math.abs(a.x - b.x) * 2 < a.width + b.width && Math.abs(a.y - b.y) * 2 < a.height + b.height;

/** Ids of the labels that survive, in draw order. */
export const cullLabels = (boxes: LabelBox[]): number[] => {
  const ordered = [...boxes].sort((a, b) => a.priority - b.priority || a.id - b.id);
  const kept: LabelBox[] = [];
  for (const box of ordered) {
    if (kept.some((other) => overlaps(box, other))) continue;
    kept.push(box);
  }
  return kept.map((box) => box.id);
};

export const labelWidth = (name: string): number => Math.min(name.length, MAX_CHARS) * CHAR_WIDTH;

interface Anchored {
  field: Field;
  latlng: L.LatLngTuple;
}

const anchors = (fields: Field[]): Anchored[] => {
  const out: Anchored[] = [];
  for (const field of fields) {
    if (!field.geometry || !field.name) continue;
    const box = bboxOf(field.geometry);
    if (!box) continue;
    out.push({ field, latlng: [(box[1] + box[3]) / 2, (box[0] + box[2]) / 2] });
  }
  return out;
};

export const attachFieldLabels = (
  map: L.Map,
  fields: Field[],
  selectedFieldId: number | null,
  theme: Theme,
): (() => void) => {
  const placed = anchors(fields);
  let markers: L.Marker[] = [];

  const clear = (): void => {
    for (const marker of markers) map.removeLayer(marker);
    markers = [];
  };

  const draw = (): void => {
    clear();
    if (placed.length === 0 || map.getZoom() < FIELD_LABEL_MIN_ZOOM) return;

    const boxes: LabelBox[] = placed.map(({ field, latlng }) => {
      const point = map.latLngToContainerPoint(latlng);
      return {
        id: field.id,
        x: point.x,
        y: point.y,
        width: labelWidth(field.name),
        height: LABEL_HEIGHT,
        priority: field.id === selectedFieldId ? 0 : 1,
      };
    });

    const visible = new Set(cullLabels(boxes));
    for (const { field, latlng } of placed) {
      if (!visible.has(field.id)) continue;
      const width = labelWidth(field.name);
      const marker = L.marker(latlng, {
        pane: PANES.labels,
        interactive: false,
        keyboard: false,
        icon: L.divIcon({
          className: "k-fieldlabel",
          iconSize: [width, LABEL_HEIGHT],
          iconAnchor: [width / 2, LABEL_HEIGHT / 2],
        }),
      });
      marker.addTo(map);
      // `textContent`, not the icon's `html`: a field name is user input and `html` is an
      // innerHTML sink.
      const element = marker.getElement();
      if (element) element.textContent = field.name;
      markers.push(marker);
    }
  };

  // The three numbers live in basemap.ts next to the contrast measurements that justify them
  // and arrive here as custom properties, so the stylesheet is not a second copy of them.
  // Only the halo WIDTH follows the theme; the pair itself must not (see MAP_LABEL_PAINT).
  const pane = map.getPane(PANES.labels);
  pane?.style.setProperty("--label-ink", MAP_LABEL_INK);
  pane?.style.setProperty("--label-halo-ink", MAP_LABEL_HALO);
  pane?.style.setProperty("--label-halo", `${MAP_LABEL_PAINT[theme].haloWidth}px`);

  draw();
  map.on("moveend", draw);
  map.on("zoomend", draw);
  return () => {
    map.off("moveend", draw);
    map.off("zoomend", draw);
    clear();
  };
};
